import React, { useEffect, useMemo, useState } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from '../../utils/axiosConfig';

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEKDAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const toDateOnly = (date) => date.toISOString().slice(0, 10);
const formatMonth = (date) => new Intl.DateTimeFormat('es-AR', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(date);

const buildGrid = (viewDate) => {
  const first = new Date(Date.UTC(viewDate.getUTCFullYear(), viewDate.getUTCMonth(), 1));
  const offset = (first.getUTCDay() + 6) % 7;
  const start = new Date(first.getTime() - offset * DAY_MS);
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(start.getTime() + index * DAY_MS);
    return { date, dateOnly: toDateOnly(date), inMonth: date.getUTCMonth() === viewDate.getUTCMonth() };
  });
};

export default function VenueAvailability({ slug }) {
  const navigate = useNavigate();
  const [viewDate, setViewDate] = useState(() => {
    const now = new Date();
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  });
  const [occupiedDates, setOccupiedDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDate, setSelectedDate] = useState('');

  const days = useMemo(() => buildGrid(viewDate), [viewDate]);
  const occupied = useMemo(() => new Set(occupiedDates), [occupiedDates]);
  const today = toDateOnly(new Date());

  useEffect(() => {
    let active = true;
    const fetchAvailability = async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await axios.get(`/${slug}/api/quotes/availability`, {
          params: { from: days[0].dateOnly, to: days[days.length - 1].dateOnly },
        });
        if (active) setOccupiedDates(data.occupiedDates || []);
      } catch (requestError) {
        if (active) setError(requestError.response?.data?.error || 'No pudimos cargar la disponibilidad.');
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchAvailability();
    return () => { active = false; };
  }, [slug, days]);

  const changeMonth = (amount) => {
    setSelectedDate('');
    setViewDate((current) => new Date(Date.UTC(current.getUTCFullYear(), current.getUTCMonth() + amount, 1)));
  };

  return (
    <section id="disponibilidad" className="mb-24 scroll-mt-24">
      <div className="mb-8 flex flex-col justify-between gap-5 border-b border-white/10 pb-6 sm:flex-row sm:items-end">
        <div>
          <p className="mb-3 text-[11px] font-black uppercase tracking-[2px] text-primary-orange">Reservas</p>
          <h2 className="text-3xl font-black text-white sm:text-4xl">Consultá la disponibilidad</h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/55">Elegí una fecha disponible para enviar una consulta. La solicitud no reserva el día hasta que el salón la confirme.</p>
        </div>
        <div className="flex gap-4 text-xs font-bold text-white/60">
          <span className="inline-flex items-center gap-2"><i className="h-2.5 w-2.5 bg-emerald-400" />Disponible</span>
          <span className="inline-flex items-center gap-2"><i className="h-2.5 w-2.5 bg-red-400" />Ocupado</span>
        </div>
      </div>

      <div className="border border-white/10 bg-[#111111]">
        <div className="flex items-center justify-between border-b border-white/10 p-4 sm:p-5">
          <button type="button" onClick={() => changeMonth(-1)} className="inline-flex h-10 w-10 items-center justify-center border border-white/10 text-white/70 hover:bg-white/5" aria-label="Mes anterior"><ChevronLeft size={19} /></button>
          <h3 className="capitalize text-lg font-black text-white">{formatMonth(viewDate)}</h3>
          <button type="button" onClick={() => changeMonth(1)} className="inline-flex h-10 w-10 items-center justify-center border border-white/10 text-white/70 hover:bg-white/5" aria-label="Mes siguiente"><ChevronRight size={19} /></button>
        </div>

        <div className="grid grid-cols-7 border-b border-white/10">
          {WEEKDAYS.map((weekday) => <span key={weekday} className="py-3 text-center text-[10px] font-black uppercase tracking-[1px] text-white/35">{weekday}</span>)}
        </div>

        {loading ? (
          <div className="flex h-[336px] items-center justify-center text-primary-orange"><Loader2 className="animate-spin" size={28} /></div>
        ) : (
          <div className="grid grid-cols-7">
            {days.map((day) => {
              const isPast = day.dateOnly < today;
              const isOccupied = occupied.has(day.dateOnly);
              const available = day.inMonth && !isPast && !isOccupied;
              const selected = selectedDate === day.dateOnly;
              return (
                <button
                  key={day.dateOnly}
                  type="button"
                  disabled={!available}
                  onClick={() => setSelectedDate(day.dateOnly)}
                  className={`relative min-h-14 border-b border-r border-white/5 p-2 text-left text-sm transition-colors sm:min-h-20 sm:p-3 ${!day.inMonth ? 'bg-black/20 text-white/15' : isPast ? 'text-white/20' : isOccupied ? 'bg-red-500/[0.06] text-red-200/70' : selected ? 'bg-primary-orange text-white' : 'text-white hover:bg-white/5'} disabled:cursor-not-allowed`}
                >
                  <span className="font-black">{day.date.getUTCDate()}</span>
                  {day.inMonth && !isPast ? <span className={`mt-1 block text-[9px] font-bold uppercase tracking-[1px] ${selected ? 'text-white/80' : isOccupied ? 'text-red-300/70' : 'text-emerald-400'}`}>{isOccupied ? 'Ocupado' : 'Libre'}</span> : null}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {error ? <p className="mt-4 text-sm font-semibold text-red-300">{error}</p> : null}
      {selectedDate ? (
        <div className="mt-5 flex flex-col justify-between gap-4 border border-primary-orange/35 bg-primary-orange/10 p-5 sm:flex-row sm:items-center">
          <div>
            <p className="text-sm font-black text-white">Fecha seleccionada: {new Intl.DateTimeFormat('es-AR', { dateStyle: 'full', timeZone: 'UTC' }).format(new Date(`${selectedDate}T00:00:00Z`))}</p>
            <p className="mt-1 text-xs font-medium text-white/60">Completá los datos para que el salón prepare una respuesta.</p>
          </div>
          <button type="button" onClick={() => navigate(`/${slug}/quote?date=${selectedDate}`)} className="inline-flex h-11 shrink-0 items-center justify-center gap-2 bg-primary-orange px-4 text-sm font-black text-white hover:bg-orange-500"><CalendarDays size={17} />Solicitar presupuesto</button>
        </div>
      ) : null}
    </section>
  );
}
